import React, { FC, useEffect, useState } from "react";
import {
  Button,
  Form,
  InputNumber,
  Modal,
  Table,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  GetOrderDetailData,
  GetPrepareShipResponseData,
  GetPrepareShipResponseOrder,
} from "services/interface";
import { formatDate, formatDateWithTime } from "utils/dateFormat";

import ModalOrderDetail from "./ModalOrderDetail";
import { useAppDispatch, useAppSelector } from "../../../store";
import { setLoading } from "store/slices/loadingSlice";

import ExportToExcel from "./ExportToExcel";
import { useGetTransportOrderDetail } from "hooks/useShipping";
import ModalPackingOrder from "./ModalPackingOrder";
import {
  setDataPrepareShippedLimit,
  setDataPrepareShippedOffset,
} from "store/slices/orderSlice";
import { CreditCardOutlined, PrinterOutlined } from "@ant-design/icons";
import ModalBoxCount from "./ModalBoxCount";
import { useGetOrderDetail } from "hooks/useOrders";

interface OrderTableListProps {
  data?: GetPrepareShipResponseData;
  IsHiddenPersonInfo?: boolean;
  refetchData: () => void;
  childrenBank: React.ReactNode[];
  refetchPrepare?: () => void;
}

const OrderTableList: FC<OrderTableListProps> = ({
  data,
  IsHiddenPersonInfo,
  refetchData,
  childrenBank,
}) => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const offset = useAppSelector(
    (state) => state.order.dataPrepareShipped.offset
  );
  const limit = useAppSelector((state) => state.order.dataPrepareShipped.limit);
  const [selectedRecords, setSelectedRecords] = useState<
    GetPrepareShipResponseOrder[]
  >([]);
  const [refId, setRefId] = useState<string>("");
  const [showEdit, setShowEdit] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [showPacking, setShowPacking] = useState<boolean>(false);
  const [modalData, setModalData] = useState<GetOrderDetailData>();
  const { mutate } = useGetTransportOrderDetail();
  const [isProductProblem, setIsProductProblem] = useState<boolean>(false);
  const [isModalLabel, setisModalLabel] = useState(false);

  const [boxcount, setBoxcount] = useState(1);
  const [orderId, setOrderId] = useState<string>("");

  const [oderDetail, setOderDetail] = useState<GetOrderDetailData>();

  const { mutateAsync: oderDetailMutate } = useGetOrderDetail();

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(Math.floor(offset / limit) + 1);
  }, [offset, limit]);

  const onSelectChange = (
    newSelectedRecords: GetPrepareShipResponseOrder[]
  ) => {
    setSelectedRecords(newSelectedRecords);
  };

  const rowSelection = {
    selectedRowKeys: selectedRecords.map((record) => record.order_id),
    onChange: (_: any, selectedRows: GetPrepareShipResponseOrder[]) =>
      onSelectChange(selectedRows),
  };

  const hasSelected = selectedRecords.length > 0;

  const showPackingModal = () => {
    setShowPacking(true);
  };

  const showEditModal = (order_id: string, ref_id: string) => {
    setShowEdit(true);
    setRefId(ref_id);
  };

  const showPrintLabel = () => {
    setisModalLabel(true);
  };

  const columns: ColumnsType<GetPrepareShipResponseOrder> = [
    {
      title: "Order No.",
      dataIndex: "order_code",
      key: "order_id",
      width: 200,
      fixed: "left",
      render: (_text, record) => (
        <Typography.Link
          style={{
            cursor: "pointer",
            fontWeight: "400",
            textDecoration: "underline",
          }}
          type="secondary"
          onClick={async () => {
            dispatch(setLoading(true));
            mutate(
              { order_id: record.order_id, status: "inprocess" },
              {
                onSuccess: async (data) => {
                  setModalData(data.data[0]);

                  await oderDetailMutate(
                    {
                      key_type: "order_id",
                      group_by: "factory",
                      items: [record.order_id],
                    },
                    {
                      onSuccess: (data) => {
                        setOderDetail(data.data[0]);
                      },
                      onError: (error) => {
                        console.log(error);
                        dispatch(setLoading(false));
                      },
                    }
                  );


                  await new Promise((resolve) => setTimeout(resolve, 200));

                  setShowDetail(true);

                  dispatch(setLoading(false));


                },
                onError: (error) => {
                  dispatch(setLoading(false));
                },
              }
            );
          }}
        >
          {_text}
        </Typography.Link>
      ),
    },
    {
      title: "วันที่สั่งซื้อ",
      dataIndex: "order_date",
      width: 200,
      render: (_text) => (
        <Typography.Text>{formatDateWithTime(_text)}</Typography.Text>
      ),
    },
    {
      title: "วันที่จัดส่ง",
      dataIndex: "due_date",
      width: 200,
      render: (_text) => (
        <Typography.Text>{formatDate(_text)}</Typography.Text>
      ),
    },
    {
      title: "ประเภทการจัดส่ง",
      dataIndex: "transport_name",
      key: "transport_name",
      width: 200,
      render: (_text) => <Typography.Text>{_text}</Typography.Text>,
    },
    {
      title: "ชื่อผู้รับสินค้า",
      dataIndex: "address_name",
      key: "address_name",
      width: 200,
      render: (_text) => <Typography.Text>{_text}</Typography.Text>,
    },
    {
      title: "เบอร์โทรศัพท์",
      dataIndex: "address_telephone",
      key: "address_telephone",
      width: 200,
      render: (_text) => <Typography.Text>{_text}</Typography.Text>,
    },
    {
      title: "จังหวัด",
      dataIndex: "province_name",
      key: "province_name",
      width: 200,
      render: (_text) => <Typography.Text>{_text}</Typography.Text>,
    },
    {
      title: "จุดจอด",
      dataIndex: "address_remark",
      key: "address_remark",
      width: 200,
      render: (_text) => <Typography.Text>{_text}</Typography.Text>,
    },
    // {
    //   title: "พิมพ์",
    //   align: "center",
    //   fixed: "right",
    //   render: (_text, record) => (
    //     <div style={{ display: "flex", flexDirection: "row", gap: 4 }}>
    //       {record?.transport_type === "post_office" && (
    //         <Tooltip title="พิมพ์ใบส่งของ">
    //           <Button
    //             size="small"
    //             type="default"
    //             onClick={() => {
    //               window.open(`/print-deliverynote/${record?.order_id}`);
    //             }}
    //           >
    //             <PrinterOutlined />
    //           </Button>
    //         </Tooltip>
    //       )}

    //       {record?.transport_type === "post_office" && (
    //         <Tooltip title="พิมพ์ Label">
    //           <Button
    //             size="small"
    //             type="default"
    //             onClick={() => {
    //               showPrintLabel();
    //               setOrderId(record?.order_id);
    //             }}
    //           >
    //             <CreditCardOutlined />
    //           </Button>
    //         </Tooltip>
    //       )}
    //     </div>
    //   ),
    // },
  ];

  let filtersColumns = columns;
  if (IsHiddenPersonInfo) {
    filtersColumns = columns.filter(
      (column) => column.key !== "ref_name" && column.key !== "ref_telephone"
    );
  }

  return (
    <div>
      <div
        style={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          marginBottom: 16,
        }}
      >
        <div style={{ flex: 1 }}></div>
        <div style={{ flex: 1, display: "flex", justifyContent: "flex-end" }}>
          <ExportToExcel />
        </div>
        {/* <Button
          type="primary"
          onClick={showPackingModal}
          disabled={!hasSelected}
        >
          ยืนยันการจัดกล่อง
        </Button> */}
      </div>
      <Table
        rowKey="order_id"
        size="small"
        // rowSelection={{
        //   ...rowSelection,
        // }}
        columns={filtersColumns}
        dataSource={data?.order_data}
        pagination={{
          current: currentPage,
          pageSize: limit,
          total: parseInt(data?.total_record as string),
          onChange: (page, pageSize) => {
            dispatch(setDataPrepareShippedOffset(pageSize! * (page! - 1)));
            dispatch(setDataPrepareShippedLimit(pageSize!));
          },
        }}
      />
      {/* <ModalPackingOrder
        form={form}
        open={showPacking}
        onClose={() => {
          setShowPacking(false);
        }}
        selectedRecords={selectedRecords}
        setSelectedRecords={setSelectedRecords}
        item={modalData as GetOrderDetailData}
      /> */}
      <ModalOrderDetail
        open={showDetail}
        onClose={() => {
          setShowDetail(false);
        }}
        item={modalData as GetOrderDetailData}
        childrenBank={childrenBank}
        refetchData={refetchData}
        statusPage="new_order"
        showEditModal={showEditModal}
        isProductProblem={isProductProblem}
        oderDetail={oderDetail}
      />
      <ModalBoxCount
        isModalLabel={isModalLabel}
        setisModalLabel={setisModalLabel}
        orderId={orderId}
      />
    </div>
  );
};

export default OrderTableList;
